import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  clampSidePanelWidth,
  persistSidePanelWidth,
  readStoredSidePanelWidth,
} from "../lib/sidePanelWidth";

/**
 * Desktop side-panel width: persisted, pointer-drag resizable.
 * Width is measured from the right edge of the viewport.
 */
export function useSidePanelWidth() {
  const [width, setWidth] = useState(readStoredSidePanelWidth);
  const [isDragging, setIsDragging] = useState(false);
  const widthRef = useRef(width);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const onResizePointerDown = useCallback((event: ReactPointerEvent) => {
    event.preventDefault();
    setIsDragging(true);

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";

    const onMove = (moveEvent: PointerEvent) => {
      const next = clampSidePanelWidth(window.innerWidth - moveEvent.clientX);
      widthRef.current = next;
      setWidth(next);
    };

    const onUp = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      persistSidePanelWidth(widthRef.current);
      setIsDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  return { width, isDragging, onResizePointerDown };
}
